import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Typography,
  Box,
  CircularProgress
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useGooglePlaces } from "../../hooks/useGooglePlaces";
import { useDuplicateCheck } from "../../hooks/useDuplicateCheck";
import { RefreshResult, RefreshStatus } from "./types";

interface PlaceIdRefreshModalProps {
  open: boolean;
  onClose: () => void;
  placeId: string;
  currentSpotId?: string;
  onUpdate: (newPlaceId: string) => void;
}

export const PlaceIdRefreshModal: React.FC<PlaceIdRefreshModalProps> = ({
  open,
  onClose,
  placeId,
  currentSpotId,
  onUpdate
}) => {
  const [result, setResult] = useState<RefreshResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { refreshPlaceId, loading } = useGooglePlaces();
  const { checkPlaceDuplicate, fetchCidFromPlaceId } = useDuplicateCheck();

  const handleRefresh = async () => {
    setError(null);
    setResult(null);

    try {
      const response = await refreshPlaceId(placeId);

      // response.statusはstring型だが、APIコントラクトでRefreshStatusの値が保証されている
      const status = response.status as RefreshStatus;
      let message = "";

      switch (status) {
        case "updated": {
          // 更新された場合、新しいplace_idの重複チェックを実行
          const newPlaceId = response.new_place_id;

          // 新しいplace_idからcidを取得
          const newCid = await fetchCidFromPlaceId(newPlaceId);

          // カスタムフックで重複チェック（place_id + cid）
          const existingSpotId = await checkPlaceDuplicate(newPlaceId, newCid, currentSpotId);

          // 他のスポットで既に使用されているかチェック
          if (existingSpotId) {
            message = `更新後のplace_id/cidは既に別のスポット（${existingSpotId}）で使用されています。`;
            setResult({
              status: "updated",
              message
            });
            setError(message);
            return;
          }

          message = "place_idを更新しました";
          onUpdate(newPlaceId);
          break;
        }
        case "unchanged":
          message = "place_idは最新です";
          break;
        case "not_found":
          message = "このplace_idはGoogle側で無効になっています（閉業の可能性があります）";
          break;
      }

      setResult({
        status,
        message
      });

      // 成功時（updated/unchanged）はalertで表示してから閉じる
      if (status === "updated" || status === "unchanged") {
        alert(message);
        onClose();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "place_idの洗い替えに失敗しました";
      setError(errorMessage);
    }
  };

  const handleClose = () => {
    setResult(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {result && result.status === "not_found" && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {result.message}
          </Alert>
        )}
        {!result && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <WarningAmberIcon color="warning" sx={{ mt: 0.5 }} />
            <Box>
              <Typography variant="body1" gutterBottom>
                place_idを最新の値に更新しますか?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Google側でplace_idが変更されている場合に更新されます
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {result ? "閉じる" : "キャンセル"}
        </Button>
        {!result && (
          <Button
            onClick={handleRefresh}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            実行
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
